/**
 * diagnosticsReport — 把緩衝 + 環境 + 上下文組成一份可交出的診斷(R249 Step 1)
 * =============================================================================
 *
 * 本檔是**純推導層**:不讀 store、不碰 DOM、不發請求 —— 所有外部值由呼叫端注入
 * ({@link DiagnosticsInput})。這樣「payload 長什麼形狀」可以逐條單測,而它同時就是
 * `POST /api/feedback/reports` 的 `diagnostics_b64` 內容 schema(R255 定案;
 * 規劃期曾寫 `/api/support/reports`,實際 ship 的端點是 feedback 這支)。
 *
 * ## 兩種輸出,同一份資料
 *
 * - {@link buildDiagnosticsReport} → 結構化物件(Step 2 送給 BE 的 body)
 * - {@link formatDiagnosticsText} → 可讀文字 + 尾段完整 JSON(Step 1 的「複製診斷碼」)
 *
 * 兩者永遠同源。對話框的「看看會送出什麼」顯示的**就是**這份文字 —— 使用者看到的與
 * 交出去的是同一個字串,這是這個功能能不能被信任的前提(尤其它會被貼進群組)。
 *
 * ## 為什麼可讀段排在 JSON 前面
 *
 * 診斷碼的第一個讀者是**人**(內部人員貼進群組、Bryan 掃一眼),第二個才是後端與 AI。
 * 純 JSON 貼進 Slack 沒有人會讀;先給「哪個畫面、什麼錯誤碼、最近幾支請求」的摘要,
 * 一眼就能分流是前端 bug 還是後端 500,再往下看細節。
 *
 * ## 刻意不放的欄位
 *
 * `cuid` / `email` / 使用者名稱**一律不進**這份 payload。Step 2 的 BE 從 session 自己
 * 取(比 FE 送的可信),而 Step 1 的診斷碼會被貼到群組 —— 不該夾帶身分。
 * `groupId` / `projectId` 有放:那是 debug 的必要座標,且是回報者自己的資源。
 */

import type { Annotation } from './annotationModel';
import { formatAnnotation } from './annotationModel';
import type { DiagnosticsApiEntry, DiagnosticsConsoleEntry } from './diagnosticsBuffer';
import { normalizeApiPath } from './diagnosticsBuffer';

/**
 * 回報分類 —— **值由 BE 定義**(docs/94 §2.2 的七選一),不合法會 422。
 *
 * 值是 wire enum,**翻譯只作用在標籤**(`common.json` 的 `report.category.*`):
 * 不同語系的使用者送出的必須是同一個值,否則後端的分類統計會依回報者的語言分裂。
 *
 * 定義放在這一層(而不是 `feedbackSubmission`)是為了避免循環相依:那支要 import
 * `formatDiagnosticsText`,所以依賴方向只能是 feedbackSubmission → 這裡。
 */
export const REPORT_CATEGORIES = [
  'bug',
  'ui',
  'generation',
  'performance',
  'billing',
  'feature_request',
  'other',
] as const;
export type ReportCategory = (typeof REPORT_CATEGORIES)[number];

/** 呼叫端注入的一切外部狀態。 */
export interface DiagnosticsInput {
  /** `RPT-` + ULID。**前端生**,Step 2 由 BE 沿用當 doc id — 見檔尾說明。 */
  reportId: string;
  nowMs: number;
  /** 使用者選填。 */
  category: ReportCategory | null;
  note: string;
  /** runtime config(`@/config`)。 */
  appEnv: string;
  buildVersion: string;
  /** `navigator.userAgent`。 */
  userAgent: string;
  language: string;
  /** 解析後的實際主題(不是偏好值 — 要知道畫面當下是深還是淺)。 */
  theme: string;
  viewportWidth: number;
  viewportHeight: number;
  /** `location.pathname + search`;本檔負責正規化(query 值會被遮掉)。 */
  rawRoute: string;
  groupId: string | null;
  projectId: string | null;
  activeJobs: readonly DiagnosticsActiveJob[];
  apiTrail: readonly DiagnosticsApiEntry[];
  consoleTrail: readonly DiagnosticsConsoleEntry[];
  /** 使用者附的截圖(≤3;各自帶自己的圈選框)。 */
  shots?: readonly DiagnosticsShotEntry[];
}

export interface DiagnosticsActiveJob {
  projectId: string;
  jobId: string;
  status: string;
  /** 已完成 / 總步數;拿不到就 null(不編數字)。 */
  progress: string | null;
}

/**
 * 使用者附上的截圖(base64)。
 *
 * ⚠️ **這是整份 payload 裡唯一的高 PII 面** —— 一張畫面含商品名、客戶名稱、金額。
 * 之所以可以送,是因為它與其他欄位性質不同:**使用者主動附加,而且看得到自己附了什麼**
 * (對話框就顯示著那張圖)。這是知情同意,不是自動蒐集。
 *
 * 兩條紀律:①**永遠不要自動附截圖** —— 必須是使用者按了「附上畫面截圖」
 * ②截圖**不進**診斷碼文字(只寫尺寸摘要)—— 診斷碼是要貼進聊天室的,夾一段十萬字元的
 * base64 既貼不出去,也等於把畫面內容散佈到使用者沒預期的地方。
 */
export interface DiagnosticsShot {
  mime: string;
  /** 純 base64(不含 `data:` 前綴 —— 前綴由 BE 依 `mime` 自己組)。 */
  dataBase64: string;
  width: number;
  height: number;
  bytes: number;
}

/**
 * 一張截圖 + 它自己的圈選框(FB-20260820-0008,海易吳承暐:「希望回報問題可以附上多張
 * 截圖(怕太多可以限制最多三張)」)。
 *
 * 🔴 **標註必須跟著它那張圖走**,不能留在 report 頂層:多張之後「第 3 個框」到底畫在
 * 哪一張上沒有辦法從一個攤平的陣列還原,而框的座標是給人與 AI 對位用的 —— 對錯了圖
 * 比沒有更糟。三張的上限來自 BE(`MAX_ATTACHMENTS = 3`),與回報者提的數字剛好一致。
 */
export interface DiagnosticsShotEntry {
  shot: DiagnosticsShot;
  annotations: readonly Annotation[];
}

export interface DiagnosticsReport {
  /** payload 形狀版本。BE 收到未知版本應照收不擋(前端可能比後端新)。 */
  schemaVersion: 2;
  reportId: string;
  createdAt: string;
  category: ReportCategory | null;
  note: string;
  client: {
    appEnv: string;
    buildVersion: string;
    userAgent: string;
    language: string;
    theme: string;
    viewport: string;
  };
  context: {
    route: string;
    groupId: string | null;
    projectId: string | null;
    activeJobs: readonly DiagnosticsActiveJob[];
  };
  apiTrail: readonly DiagnosticsApiEntry[];
  consoleTrail: readonly DiagnosticsConsoleEntry[];
  /** 使用者附的截圖(≤3);沒附就是空陣列。每張各自帶自己的圈選框。 */
  shots: readonly DiagnosticsShotEntry[];
}

/**
 * payload 的頂層欄位集合 —— **機械閘**(`diagnosticsReport.test.ts` 逐字比對)。
 *
 * 存在的理由不是形式主義:這份 payload 會被貼進群組聊天、寫進後端 log。「多加一個欄位」
 * 是最容易發生的洩漏方式(某天有人為了方便 debug 加上 `user` 或 `slots`),而且不會有任何
 * 徵兆。加欄位讓測試紅 = 強迫加的人在 PR 上寫出「這一欄可不可以公開」。
 *
 * `satisfies` 讓它與型別綁在一起:漏列 / 多列一個 key,tsc 當場紅。
 */
export const DIAGNOSTICS_REPORT_KEYS = [
  'schemaVersion',
  'reportId',
  'createdAt',
  'category',
  'note',
  'client',
  'context',
  'apiTrail',
  'consoleTrail',
  'shots',
] as const satisfies readonly (keyof DiagnosticsReport)[];

/**
 * payload 形狀版本。**2**(FB-20260820-0008)= `shot` + `annotations` 兩個頂層欄位
 * 換成 `shots[]`(每張各自帶框)。BE 對這份 blob 不解讀語意(只做去敏與大小閘),
 * 所以升版不需要它配合;寫在這裡是給讀 admin 介面的人一個判斷依據。
 */
export const DIAGNOSTICS_SCHEMA_VERSION = 2;

/** `RPT-` 前綴讓它在 Slack 訊息裡一眼可辨,也方便後端 grep。 */
export function newReportId(clientRequestId: string): string {
  return `RPT-${clientRequestId}`;
}

export function buildDiagnosticsReport(input: DiagnosticsInput): DiagnosticsReport {
  return {
    schemaVersion: DIAGNOSTICS_SCHEMA_VERSION,
    reportId: input.reportId,
    createdAt: new Date(input.nowMs).toISOString(),
    category: input.category,
    note: input.note.trim(),
    client: {
      appEnv: input.appEnv,
      buildVersion: input.buildVersion,
      userAgent: input.userAgent,
      language: input.language,
      theme: input.theme,
      viewport: `${input.viewportWidth}x${input.viewportHeight}`,
    },
    context: {
      // route 走同一支正規化:`/projects/01K…/canvas` → `/projects/{id}/canvas`,
      // 且 query 值(可能含搜尋字串)被遮掉。真正的 pid 在下面具名欄位。
      route: normalizeApiPath(input.rawRoute),
      groupId: input.groupId,
      projectId: input.projectId,
      activeJobs: input.activeJobs,
    },
    apiTrail: input.apiTrail,
    consoleTrail: input.consoleTrail,
    // 標註住在各自那張圖裡 —— 結構上就不可能出現「標註指向一張不存在的圖」。
    shots: input.shots ?? [],
  };
}

// ─── 可讀文字 ───────────────────────────────────────────────────────────────

/** 可讀段只列最近 N 筆;完整清單在尾段 JSON(避免貼進 Slack 被長度截斷後失去重點)。 */
export const TEXT_TRAIL_ROWS = 15;

function timeOf(iso: string): string {
  // `2026-08-18T10:23:41.002Z` → `10:23:41.002`(同一份回報的時間都在同一天,
  // 只留時分秒讓表格對得整齊;完整 ISO 在 JSON 段)。
  const m = /T(\d{2}:\d{2}:\d{2}\.\d{3})/.exec(iso);
  return m?.[1] ?? iso;
}

function pad(s: string, width: number): string {
  return s.length >= width ? s : s + ' '.repeat(width - s.length);
}

function apiLine(e: DiagnosticsApiEntry): string {
  const status = e.status === null ? 'ERR' : String(e.status);
  const code = e.errorCode ? ` ${e.errorCode}` : '';
  return `  ${timeOf(e.ts)}  ${pad(e.method, 6)}${pad(status, 4)} ${e.path}${code}  ${e.durationMs}ms  ${e.clientRequestId}`;
}

/**
 * 組出使用者按「複製診斷碼」拿到的字串。
 *
 * 標籤刻意用**英文短詞**而非 i18n:這串會被貼給後端 / AI 讀,固定欄名比在地化更有用,
 * 而且不會因為回報者的語系不同而讓後端看到三種格式(對齊 wire 值不翻譯的既有慣例)。
 */
export function formatDiagnosticsText(report: DiagnosticsReport): string {
  const L: string[] = [];
  L.push(`HAi diagnostics ${report.reportId}`);
  L.push('='.repeat(60));
  L.push(`time      ${report.createdAt}`);
  L.push(
    `env       ${report.client.appEnv} · build ${report.client.buildVersion} · ${report.client.language} · ${report.client.theme}`,
  );
  L.push(`screen    ${report.context.route}   (${report.client.viewport})`);
  L.push(`ua        ${report.client.userAgent}`);
  if (report.context.groupId) L.push(`group     ${report.context.groupId}`);
  if (report.context.projectId) L.push(`project   ${report.context.projectId}`);
  for (const j of report.context.activeJobs) {
    L.push(`job       ${j.jobId} ${j.status}${j.progress ? ` (${j.progress})` : ''}`);
  }
  if (report.category) L.push(`category  ${report.category}`);
  if (report.note) L.push(`note      ${report.note}`);

  const trail = report.apiTrail;
  const shown = trail.slice(-TEXT_TRAIL_ROWS);
  L.push('');
  L.push(
    trail.length > shown.length
      ? `api trail (last ${shown.length} of ${trail.length}; full list in JSON below)`
      : `api trail (${trail.length})`,
  );
  if (shown.length === 0) L.push('  (none)');
  for (const e of shown) L.push(apiLine(e));

  L.push('');
  L.push(`console (${report.consoleTrail.length})`);
  if (report.consoleTrail.length === 0) L.push('  (none)');
  for (const c of report.consoleTrail) {
    L.push(`  ${timeOf(c.ts)}  ${c.level}  ${c.message}`);
    if (c.stack) {
      for (const line of c.stack.split('\n').slice(0, 6)) L.push(`      ${line.trim()}`);
    }
  }

  if (report.shots.length > 0) {
    L.push('');
    // ⚠️ 只寫摘要不寫 base64:診斷碼是要貼進聊天室的字串。
    report.shots.forEach((entry, i) => {
      const s = entry.shot;
      L.push(
        `screenshot ${i + 1}/${report.shots.length}  ${s.width}x${s.height} ${s.mime} ${Math.round(s.bytes / 1024)}KB`,
      );
      for (const a of entry.annotations) L.push(`  circled  ${formatAnnotation(a)}`);
    });
  }

  L.push('');
  L.push('--- json ---');
  // JSON 段同樣剝掉 base64(理由同上;BE 收到的是完整 payload,診斷碼是給人貼的)。
  L.push(
    JSON.stringify({
      ...report,
      shots: report.shots.map((e) => ({
        ...e,
        shot: { ...e.shot, dataBase64: `<${e.shot.bytes} bytes omitted>` },
      })),
    }),
  );
  return L.join('\n');
}
